import { Button, Modal, Row, Spin, Tooltip } from "antd";
import Title from "antd/lib/typography/Title";
import { MouseEventHandler } from "react";
import { CiGlobe } from "react-icons/ci";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaWhatsapp } from "react-icons/fa6";
import { formatCep, formatCnae, formatCnpj, formatPhone, porteEmpresa, SearchResultData } from "../../router/SearchResultTable";
import { subclasseSelect } from "../../config/data";

type CompanySummaryProps = {
    isModalVisible: boolean;
    handleClose: MouseEventHandler<HTMLElement>;
    modalLoading: boolean;
    selectedRecord: SearchResultData | null;
    apiResponse: Record<string, any> | null;
  };
  
  const CompanySummary: React.FC<CompanySummaryProps> = ({
    isModalVisible,
    handleClose,
    modalLoading,
    selectedRecord,
    apiResponse,
  }) => {
    return(
        <Modal
        title="Resumo da Empresa"
        centered
        open={isModalVisible}
        width="80%"
        style={{borderRadius: '100px'}}
        onCancel={handleClose}
        footer={[
          <div style={{ width: "100%", textAlign: "center" }} key="footer">
            <Button type="primary" onClick={handleClose} className="submitButton">
              Fechar
            </Button>
          </div>,
        ]}
      >
        {modalLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <Spin tip="Gerando resumo..." spinning={modalLoading} />
          </div>
        ) : (
          <div>
            <div
              style={{
                display: "flex",
                gap: "15px",
              }}
            >
              <div className="companySummaryContainer">
                <Title level={5}>Dados Cadastrais</Title>
                {selectedRecord ? (
                  <div style={{ marginTop: "10px" }}>
                    <p>
                      <b>CNPJ:</b>{" "}
                      {selectedRecord.cnpj_completo
                        ? formatCnpj(selectedRecord.cnpj_completo)
                        : "Sem dados"}
                    </p>
                    <p>
                      <b>Razão Social:</b>{" "}
                      {selectedRecord.razao_social ?? "Sem dados"}
                    </p>
                    <p>
                      <b>Nome Fantasia:</b>{" "}
                      {selectedRecord.nome_fantasia ?? "Sem dados"}
                    </p>
                    <p>
                      <b>UF:</b> {selectedRecord.uf ?? "Sem dados"}
                    </p>
                    <p>
                      <b>Cidade:</b> {selectedRecord.municipio ?? "Sem dados"}
                    </p>
                    <p>
                      <b>CEP:</b>{" "}
                      {selectedRecord.cep
                        ? formatCep(selectedRecord.cep)
                        : "Sem dados"}
                    </p>
                    <p>
                      <b>Endereço:</b>{" "}
                      {selectedRecord.logradouro
                        ? `${selectedRecord.logradouro} ${selectedRecord.bairro}`
                        : "Sem dados"}
                    </p>
                    <p>
                      <b>CNAE:</b>{" "}
                      {selectedRecord.cnae_principal
                        ? `${formatCnae(selectedRecord.cnae_principal)} - ${subclasseSelect.find((element: any) => element.value === selectedRecord.cnae_principal)?.label || "Código não encontrado"}`
                        : "Sem dados"}
                    </p>
                    <p>
                      <b>Porte:</b>{" "}
                      {porteEmpresa[selectedRecord.porte] ?? "Sem dados"}
                    </p>
                    <p>
                      <b>Capital Social:</b>{" "}
                      {selectedRecord.capital_social ?? "Sem dados"}
                    </p>
                    <p>
                      <b>MEI:</b>{" "}
                      {(selectedRecord.mei ?? "Sem dados") === "S"
                        ? "Sim"
                        : "Não"}
                    </p>
                  </div>
                ) : (
                  <p>Nenhum dado cadastral disponível.</p>
                )}
              </div>

              <div className="companySummaryContainer">
                <Title level={5}>Dados Sociais</Title>
                {selectedRecord ? (
                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      flexDirection: "column",
                      height: "85%",
                    }}
                  >
                    <p>
                      <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                        <b>Sócios:</b>{" "}
                        {selectedRecord.socios ?? "Sem informações"}
                      </div>
                    </p>
                    <p>
                      <b>Telefone:</b>{" "}
                      {formatPhone(selectedRecord.telefone) ??
                        "Sem informações"}
                    </p>
                    <p>
                      <b>E-mail:</b> {selectedRecord.email ?? "Sem informações"}
                    </p>

                    <Row
                      style={{
                        justifyContent: "center",
                        gap: "40px",
                        marginTop: "auto",
                      }}
                    >
                      <Tooltip
                        title={
                          selectedRecord.dominio_email
                            ? selectedRecord.dominio_email
                            : "Não disponível"
                        }
                      >
                        <CiGlobe
                          style={
                            selectedRecord.dominio_email
                              ? { cursor: "pointer", color: "#33EA89" }
                              : { color: "gray" }
                          }
                          onClick={() => {
                            if (selectedRecord.dominio_email)
                              window.open(
                                `https://${selectedRecord.dominio_email}`,
                                "_blank",
                              );
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={
                          selectedRecord.linkedin
                            ? selectedRecord.linkedin
                            : "Não disponível"
                        }
                      >
                        <FaLinkedinIn
                          style={
                            selectedRecord.linkedin
                              ? { cursor: "pointer", color: "#33EA89" }
                              : { color: "gray" }
                          }
                          onClick={() => {
                            if (selectedRecord.linkedin)
                              window.open(
                                `${selectedRecord.linkedin}`,
                                "_blank",
                              );
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={
                          selectedRecord.facebook
                            ? selectedRecord.facebook
                            : "Não disponível"
                        }
                      >
                        <FaFacebookF
                          style={
                            selectedRecord.facebook
                              ? { cursor: "pointer", color: "#33EA89" }
                              : { color: "gray" }
                          }
                          onClick={() => {
                            if (selectedRecord.facebook)
                              window.open(
                                `${selectedRecord.facebook}`,
                                "_blank",
                              );
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={
                          selectedRecord.instagram
                            ? selectedRecord.instagram
                            : "Não disponível"
                        }
                      >
                        <FaInstagram
                          style={
                            selectedRecord.instagram
                              ? { cursor: "pointer", color: "#33EA89" }
                              : { color: "gray" }
                          }
                          onClick={() => {
                            if (selectedRecord.instagram)
                              window.open(
                                `${selectedRecord.instagram}`,
                                "_blank",
                              );
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={
                          selectedRecord.whatsapp
                            ? "WhatsApp no site: " + selectedRecord.whatsapp
                            : "Não disponível"
                        }
                      >
                        <FaWhatsapp
                          style={
                            selectedRecord.whatsapp
                              ? { cursor: "pointer", color: "#33EA89" }
                              : { color: "gray" }
                          }
                          onClick={() => {
                            if (selectedRecord.whatsapp)
                              window.open(
                                `${selectedRecord.whatsapp}`,
                                "_blank",
                              );
                          }}
                        />
                      </Tooltip>
                    </Row>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="companySummaryContainer">
              <Title level={5}>Inteligência Comercial</Title>
              {apiResponse?.error ? (
                <div
                  style={{
                    color: "#ff4d4f",
                    backgroundColor: "#fff1f0",
                    padding: "10px",
                    marginTop: "10px",
                    borderRadius: "4px",
                  }}
                >
                  <p>
                    <b>A Empresa não possui dados suficientes.</b>
                  </p>
                </div>
              ) : (
                (() => {
                  const parsedResponse = apiResponse ? apiResponse : {};
                  return (
                    <div style={{ marginTop: "10px" }}>
                      <p style={{ textAlign: "justify", marginBottom: "12px" }}>
                        <b>Área de Atuação:</b> {parsedResponse.area_atuacao}
                      </p>
                      <p style={{ textAlign: "justify", marginBottom: "12px" }}>
                        <b>Localização:</b> {parsedResponse.localizacao}
                      </p>
                      <p style={{ textAlign: "justify", marginBottom: "12px" }}>
                        <b>Geral:</b> {parsedResponse.geral}
                      </p>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        )}
      </Modal>
    )
}

export default CompanySummary